import { VT2_TABLE_CONFIG } from 'vio-tables-v2';

type TableBase = Omit<VT2_TABLE_CONFIG, 'actionButtons'>;

// ─── Shared defaults ─────────────────────────────────────────────────────────
// Change a property here to apply it across every table in the app.
// Each table-specific base spreads this and overrides only what differs.
export const COMMON_TABLE_BASE = {
  statusField: '',
  dateFields: [] as string[],
  numericFields: [] as string[],
  dropdownFields: [] as any[],
  computedColumns: [] as any[],
  actionColumnName: '',
  initialPageSize: 10 as number | 'all',
  freezeColumns: 0,
  isSortable: true,
  exportToExcel: true,
  hideSearch: false,
  enableInlineEdit: false,
  showTotals: false,
  showPageTotal: false,
  showGrandTotal: false,
  enableRowSelection: false,
  maxActionButtons: 3,
  showFormulaButton: false,
  showHeaderGroupButton: false,
};

// ─── Language ────────────────────────────────────────────────────────────────
export const LANGUAGE_TABLE_BASE: TableBase = {
  ...COMMON_TABLE_BASE,
  tableTitle: 'Languages List',
  excelFileName: 'languages',
  excludeFields: ['_id', 'id', '__v', 'isActive', 'createdAt'],
  columnLabels: { languageName: 'Language Name', code: 'Code' },
};

// ─── Membership Closure Reason ─────────────────────────────────────────────
export const CLOSURE_REASON_TABLE_BASE: TableBase = {
  ...COMMON_TABLE_BASE,
  tableTitle: 'Closure Reasons',
  excelFileName: 'closure-reasons',
  excludeFields: ['_id', 'id', '__v', 'isActive', 'createdAt', 'updatedAt'],
  columnLabels: { reasonName: 'Reason', activeLabel: 'Status' },
  statusField: 'activeLabel',
  statusClassMap: { 'Active': 'badge--green', 'Inactive': 'badge--red' },
};

// ─── Category ────────────────────────────────────────────────────────────────
export const CATEGORY_TABLE_BASE: TableBase = {
  ...COMMON_TABLE_BASE,
  tableTitle: 'Category List',
  excelFileName: 'category',
  excludeFields: ['_id', 'id', 'internalCode', 'isActive', '__v', 'createdAt', 'description'],
  columnLabels: { categoryName: 'Category Name', categoryNameEnglish: 'English Name' },
};

// ─── Author ──────────────────────────────────────────────────────────────────
export const AUTHOR_TABLE_BASE: TableBase = {
  ...COMMON_TABLE_BASE,
  tableTitle: 'Authors List',
  excelFileName: 'authors',
  excludeFields: [
    '_id', 'id', '__v', 'isActive', 'createdAt', 'authorSrNo',
    'country', 'biography', 'authorAddress1', 'authorAddress2', 'authorAddress3', 'pincode', 'mobileNo',
  ],
  columnLabels: {
    authorCode: 'Code',
    authorName: 'Author Name',
    authorEnglishName: 'English Name',
    dateOfBirth: 'Date of Birth',
  },
  dateFields: ['dateOfBirth'],
};

// ─── Center ──────────────────────────────────────────────────────────────────
export const CENTER_TABLE_BASE: TableBase = {
  ...COMMON_TABLE_BASE,
  tableTitle: 'Centers List',
  excelFileName: 'centers',
  excludeFields: ['_id', 'id', '__v', 'managerId', 'pincode', 'isActive', 'createdAt'],
  columnLabels: {
    centerId: 'Center ID',
    centerName: 'Center Name',
    area: 'Area',
    address: 'Address',
    managerName: 'Manager',
  },
};

// ─── Route ───────────────────────────────────────────────────────────────────
export const ROUTE_TABLE_BASE: TableBase = {
  ...COMMON_TABLE_BASE,
  tableTitle: 'Routes List',
  excelFileName: 'routes',
  excludeFields: ['_id', 'id', '__v', 'runnerid', 'createdAt'],
  columnLabels: {
    routeid: 'Route ID',
    description: 'Description',
    dataofrun: 'Day of Run',
    isActive: 'Status',
  },
  statusField: 'isActive',
  numericFields: ['dataofrun'],
};

// ─── User ─────────────────────────────────────────────────────────────────────
export const USER_TABLE_BASE: TableBase = {
  ...COMMON_TABLE_BASE,
  tableTitle: 'Users List',
  excelFileName: 'users',
  excludeFields: ['_id', 'id', '__v', 'password', 'passwordHash', 'salt', 'tenantId', 'db', 'createdAt', 'token', 'refreshToken', 'isActive'],
  columnLabels: { name: 'Name', email: 'Email', role: 'Role' },
};

// ─── Member ──────────────────────────────────────────────────────────────────
export const MEMBER_TABLE_BASE: TableBase = {
  ...COMMON_TABLE_BASE,
  tableTitle: 'Members List',
  excelFileName: 'members',
  excludeFields: ['_id', 'id', '__v', 'routeId', 'locationId', 'routeDisplayId', 'isActive', 'borrowedBooks', 'landmark', 'pincode', 'createdAt', 'updatedAt', 'memberSrNo', 'closedAt', 'closureDetails'],
  columnLabels: {
    name: 'Name',
    memNo: 'Mem No',
    phone: 'Phone',
    area: 'Area',
    address: 'Address',
    memberType: 'Member Type',
    routeDesc: 'Location',
    memberSince: 'Member Since',
    membershipExpiresOn: 'Membership Expires On',
    maxBooksAllowed: 'Max Books',
    subscriptionAmount: 'Subscription',
    status: 'Member Status',
    closureReason: 'Closure Reason',
  },
  statusField: 'status',
  statusClassMap: {
    'MEMBER': 'badge--green',
    'OnHold': 'badge--orange',
    'Due':    'badge--red',
    'Closed': 'badge--red',
  },
  dateFields: ['memberSince', 'membershipExpiresOn'],
  numericFields: ['maxBooksAllowed', 'subscriptionAmount'],
};

// ─── Book ─────────────────────────────────────────────────────────────────────
export const BOOK_TABLE_BASE: TableBase = {
  ...COMMON_TABLE_BASE,
  tableTitle: 'Books List',
  excelFileName: 'books',
  excludeFields: ['_id', 'id', '__v', 'bookId', 'isActive', 'isTranslated', 'categoryNamesEnglish', 'firstPublishedIn', 'copiesInField', 'copiesAvailable', 'createdAt'],
  columnLabels: {
    bookName: 'Book Name',
    bookNameEnglish: 'Book Name (English)',
    authorNames: 'Authors',
    authorNamesEnglish: 'Authors (English)',
    categoryNames: 'Categories',
    ISBN: 'ISBN',
    price: 'Price (₹)',
    totalCopies: 'Total Copies',
  },
  numericFields: ['price', 'totalCopies'],
  showTotals: true,
};

// ─── Book Inventory (no action buttons — full config exported directly) ───────
export const BOOK_INVENTORY_TABLE_CONFIG: VT2_TABLE_CONFIG = {
  ...COMMON_TABLE_BASE,
  tableTitle: 'Book Inventory',
  excelFileName: 'book-inventory',
  excludeFields: ['status', 'copyWith', 'location'],
  columnLabels: {
    bookSrNo: 'Book Sr No',
    bookName: 'Book Name',
    bookNameEnglish: 'Book Name (English)',
    authorNames: 'Authors',
    authorNamesEnglish: 'Authors (English)',
    categoryNames: 'Categories',
    statusDisplay: 'Location',
  },
  numericFields: ['bookSrNo'],
  initialPageSize: 20,
  maxActionButtons: 0,
  actionButtons: [],
};

// ─── Receipt / Payment Report ────────────────────────────────────────────────
export const RECEIPT_PAYMENT_TABLE_CONFIG: VT2_TABLE_CONFIG = {
  ...COMMON_TABLE_BASE,
  tableTitle: 'Receipt / Payment',
  excelFileName: 'receipt-payment',
  excludeFields: [],
  columnLabels: {
    trnxSrNo:   'Rcpt No',
    trnxDate:   'Trnx Date',
    memberName: 'Member Name',
    memNo:      'Member No',
    phone:      'Phone',
    amount:     'Amount',
    trnxType:   'Transaction Type',
  },
  dateFields:           ['trnxDate'],
  numericFields:        ['amount'],
  showTotals:           true,
  showPageTotal:        true,
  showFormulaButton:    false,
  showHeaderGroupButton: false,
  initialPageSize:      20,
  maxActionButtons:     0,
  actionButtons:        [],
};

// ─── Closed Members Report ───────────────────────────────────────────────────
export const CLOSED_MEMBERS_TABLE_CONFIG: VT2_TABLE_CONFIG = {
  ...COMMON_TABLE_BASE,
  tableTitle: 'Closed Members',
  excelFileName: 'closed-members',
  excludeFields: ['_id'],
  columnLabels: {
    memNo:         'Mem No',
    name:          'Name',
    phone:         'Phone',
    area:          'Area',
    memberType:    'Member Type',
    closedOn:      'Closed On',
    closureReason: 'Closure Reason',
    remarks:       'Remarks',
    refundAmount:  'Deposit Refunded',
    refundMode:    'Refund Mode',
    refundDate:    'Refund Date',
  },
  dateFields:           ['closedOn', 'refundDate'],
  numericFields:        ['refundAmount'],
  showTotals:           true,
  showPageTotal:        true,
  showGrandTotal:       true,
  showFormulaButton:    false,
  showHeaderGroupButton: false,
  initialPageSize:      20,
  maxActionButtons:     0,
  actionButtons:        [],
};

// ─── Barcode Labels ──────────────────────────────────────────────────────────
export const BARCODE_TABLE_BASE: TableBase = {
  ...COMMON_TABLE_BASE,
  tableTitle: 'Books — Select to Print Labels',
  excelFileName: 'books',
  excludeFields: ['_id', 'id', '__v', 'isTranslated', 'authorNamesEnglish', 'categoryNames', 'categoryNamesEnglish', 'firstPublishedIn', 'copiesInField', 'isActive', 'price', 'createdAt'],
  columnLabels: {
    bookName: 'Book Name',
    bookNameEnglish: 'Book Name (English)',
    authorNames: 'Authors',
    bookId: 'Book ID',
    ISBN: 'ISBN',
    copiesAvailable: 'Copies Available',
  },
  statusField: 'isActive',
  numericFields: ['copiesAvailable'],
  exportToExcel: false,
};
